/**
 * Cliente del API de "Lo último" (tabla propia `news_entries` en el backend).
 * Todo va contra la base de datos vía REST; no hay JSON local ni localStorage.
 */
import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'

const ENDPOINT = `${getPortalApiBase()}/news`

/**
 * Cabeceras con el Bearer del admin (mismo patrón que commercials y adminApi de
 * pagos). La gestión de "Lo último" exige un admin del portal
 * verificado en portal-server (goal seguridad-remediacion 12); las lecturas
 * públicas no lo llevan.
 */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

export interface NewsEntry {
  id: string
  title: string
  date: string
  categories: string[]
  image: string
  youtubeUrl: string
  excerpt: string
  body: string
  published: boolean
  createdAt?: string
  updatedAt?: string
}

/** Campos editables (sin metadatos del servidor). */
export type NewsInput = Omit<NewsEntry, 'id' | 'createdAt' | 'updatedAt'>

export interface AdminNewsResult {
  items: NewsEntry[]
  total: number
  page: number
  pageSize: number
  categories: string[]
}

export interface AdminNewsQuery {
  page?: number
  pageSize?: number
  q?: string
  status?: 'all' | 'published' | 'draft'
  category?: string
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) throw new Error(`${ctx}: ${res.status}`)
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

/** Listado público (solo publicadas). */
export async function fetchPublishedNews(params: { category?: string; limit?: number } = {}): Promise<NewsEntry[]> {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.limit) qs.set('limit', String(params.limit))
  const res = await fetch(`${ENDPOINT}${qs.toString() ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json' },
  })
  return (await json<NewsEntry[] | null>(res, 'GET news')) ?? []
}

/** Detalle público por id (null si no existe o no está publicada). */
export async function fetchNewsById(id: string): Promise<NewsEntry | null> {
  const res = await fetch(`${ENDPOINT}/${id}`, { headers: { Accept: 'application/json' } })
  if (res.status === 404) return null
  return json<NewsEntry | null>(res, 'GET news/:id')
}

/** Listado de administración: paginado + búsqueda + filtros. */
export async function fetchAdminNews(query: AdminNewsQuery): Promise<AdminNewsResult> {
  const qs = new URLSearchParams()
  if (query.page) qs.set('page', String(query.page))
  if (query.pageSize) qs.set('pageSize', String(query.pageSize))
  if (query.q) qs.set('q', query.q)
  if (query.status && query.status !== 'all') qs.set('status', query.status)
  if (query.category && query.category !== 'all') qs.set('category', query.category)
  const res = await fetch(`${ENDPOINT}/admin?${qs}`, { headers: authHeaders() })
  const data = await json<AdminNewsResult | null>(res, 'GET news/admin')
  return data ?? { items: [], total: 0, page: 1, pageSize: query.pageSize ?? 8, categories: [] }
}

export async function createNews(input: NewsInput): Promise<NewsEntry> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<NewsEntry>(res, 'POST news')
}

export async function updateNews(id: string, input: NewsInput): Promise<NewsEntry> {
  const res = await fetch(`${ENDPOINT}/${id}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<NewsEntry>(res, 'PUT news/:id')
}

export async function deleteNews(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${id}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) throw new Error(`DELETE news/:id: ${res.status}`)
}
